import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import StakeholderRiskConcentrationRadar from './StakeholderRiskConcentrationRadar';
import { DashboardStore } from '../../stores/DashboardStore';
import { getUseCaseService } from '../../services/UseCaseService';
import type { ImpactPlusReport } from '../../entities/report/schemas/ImpactPlusReport.schema';
import {
  RISK_CONCENTRATION_TITLE,
  type HarmRank,
  type StakeholderHarmRow,
} from './stakeholderImpactAssessmentData';
import type { ImpactPlusCohortRanking } from '../../entities/report/schemas/ImpactPlusReport.schema';

function displayUseCaseTitle(title: string | undefined): string {
  const t = String(title ?? '').trim();
  if (!t || t === 'N/A' || t === '<untitled>') return 'Untitled use case';
  return t;
}

type RankFilter = 'all' | HarmRank;

const RANK_FILTERS: { key: RankFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'critical', label: 'Critical' },
  { key: 'major', label: 'High' },
  { key: 'moderate', label: 'Moderate' },
  { key: 'minor', label: 'Low' },
];

const HARM_RANK_ORDER: Record<HarmRank, number> = {
  critical: 0,
  major: 1,
  moderate: 2,
  minor: 3,
};

const RANK_PILL: Record<
  HarmRank,
  { label: string; className: string }
> = {
  critical: {
    label: 'Critical',
    className: 'bg-[var(--critical)] text-white',
  },
  major: {
    label: 'High',
    className: 'bg-[var(--high)] text-white',
  },
  moderate: {
    label: 'Moderate',
    className: 'bg-[var(--moderate)] text-white',
  },
  minor: {
    label: 'Low',
    className: 'bg-[var(--low)] text-white',
  },
};

const HS_PILL_CLASS =
  'inline-flex items-center gap-[4px] rounded-[6px] border px-[10px] py-[3px] text-xxs font-semibold';

const HS_PILL_STYLE: Record<HarmRank, { fg: string; bg: string }> = {
  critical: { fg: 'var(--critical)', bg: '#FDF0EF' },
  major: { fg: 'var(--high)', bg: '#FEF0E7' },
  moderate: { fg: 'var(--moderate)', bg: '#FFF8E1' },
  minor: { fg: 'var(--low)', bg: '#EBF7EE' },
};

function harmRankFromApi(
  riskRank: string | undefined,
): HarmRank {
  switch (String(riskRank ?? '').toLowerCase()) {
    case 'critical':
      return 'critical';
    case 'high':
      return 'major';
    case 'moderate':
      return 'moderate';
    case 'low':
    case 'negligible':
    default:
      return 'minor';
  }
}

function buildCountsFromCohortRankings(rankings: ImpactPlusCohortRanking[]): Record<HarmRank, number> {
  const counts: Record<HarmRank, number> = { critical: 0, major: 0, moderate: 0, minor: 0 };
  for (const r of rankings) {
    switch (r.riskRankLabel) {
      case 'critical': counts.critical += r.numCohorts; break;
      case 'high': counts.major += r.numCohorts; break;
      case 'moderate': counts.moderate += r.numCohorts; break;
      default: counts.minor += r.numCohorts; break;
    }
  }
  return counts;
}

function buildRowsFromReport(report: ImpactPlusReport | null): StakeholderHarmRow[] {
  if (!report) return [];

  const cohorts = report.impactedStakeholders?.impactedCohorts ?? [];

  return cohorts.map((cohort, cohortIdx) => ({
    id: `cohort-${cohortIdx}`,
    rank: harmRankFromApi(cohort.cohortRisk),
    stakeholderGroup: cohort.label || cohort.stakeholderCategory || 'Stakeholder cohort',
    hazardDescriptions: (cohort.harms ?? []).map((h) => h.description).filter(Boolean),
    harmDescription: cohort.harmSummary || cohort.description || '',
    domains: [],
    detailAndReasoning: cohort.harmJustification || cohort.harmSummary || '',
    furtherDetailAndReasoningBullets: [],
    lifecycle: '-',
    accountableParties: '-',
  }));
}

export default function ImpactPlusStakeholderImpactAssessment() {
  const { useCaseChatId = '' } = useParams<{ useCaseChatId: string }>();
  const [searchParams] = useSearchParams();
  const impactPlusReportId = searchParams.get('impactPlusReportId') ?? '';
  const impactPlusReportQuery = impactPlusReportId
    ? `?impactPlusReportId=${encodeURIComponent(impactPlusReportId)}`
    : '';
  const [selectedRanks, setSelectedRanks] = useState<Set<HarmRank>>(() => new Set());
  const stakeholderRegisterHref = `/use-case/${encodeURIComponent(useCaseChatId)}/stakeholder-register${impactPlusReportQuery}`;

  const useCaseBriefs = DashboardStore.useDashboardStore((s) => s.useCaseBriefs);

  const [impactPlusReport, setImpactPlusReport] = useState<ImpactPlusReport | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const loadImpactPlusReport = useCallback(async () => {
    if (!impactPlusReportId) {
      setImpactPlusReport(null);
      setReportError(null);
      setIsLoadingReport(false);
      return;
    }
    setReportError(null);
    setIsLoadingReport(true);
    try {
      const report = await getUseCaseService().getImpactPlusReport(impactPlusReportId);
      setImpactPlusReport(report);
    } catch (err) {
      setImpactPlusReport(null);
      setReportError(err instanceof Error ? err.message : 'Could not load Impact+ report.');
    } finally {
      setIsLoadingReport(false);
    }
  }, [impactPlusReportId]);

  useEffect(() => {
    void loadImpactPlusReport();
  }, [loadImpactPlusReport]);

  const useCaseTitle = useMemo(() => {
    const brief = useCaseBriefs.find((b) => b.useCaseChatId === useCaseChatId);
    const reportTitle = impactPlusReport?.titlePage?.useCaseTitle;
    const briefTitle = brief?.useCaseTitle;
    return displayUseCaseTitle(reportTitle || briefTitle);
  }, [useCaseBriefs, useCaseChatId, impactPlusReport]);

  const harmRows = useMemo(
    () => buildRowsFromReport(impactPlusReport).sort((a, b) => HARM_RANK_ORDER[a.rank] - HARM_RANK_ORDER[b.rank]),
    [impactPlusReport],
  );

  const counts = useMemo(
    () => buildCountsFromCohortRankings(impactPlusReport?.decisionGuidance.cohortRankings ?? []),
    [impactPlusReport],
  );
  const total = useMemo(
    () => (impactPlusReport?.decisionGuidance.cohortRankings ?? []).reduce((sum, r) => sum + r.numCohorts, 0),
    [impactPlusReport],
  );

  const summaryLine = useMemo(
    () => `${useCaseTitle} · ${total} impacts identified`,
    [useCaseTitle, total],
  );

  const filteredRows = useMemo(() => {
    if (selectedRanks.size === 0) return harmRows;
    return harmRows.filter((r: StakeholderHarmRow) => selectedRanks.has(r.rank));
  }, [selectedRanks, harmRows]);

  const stakeholderRiskConcentrationData = useMemo(() => {
    const ext = impactPlusReport?.aiSystemImpact?.external;
    return {
      people: { harmRank: ext?.people.harmRank ?? 'negligible' },
      organisations: { harmRank: ext?.organisations.harmRank ?? 'negligible' },
      ecosystems: { harmRank: ext?.ecosystems.harmRank ?? 'negligible' },
    };
  }, [impactPlusReport]);

  const riskConcentrationHtml = useMemo(
    () => impactPlusReport?.aiSystemImpactSummary?.riskConcentrationSummary ?? '',
    [impactPlusReport],
  );

  if (!useCaseChatId) return null;

  return (
    <div className="min-h-screen bg-impact-plus-page pb-12 pt-2">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/dashboard"
            className="line-height-normal flex items-center gap-2 text-sm font-medium text-royal-blue hover:text-deep-blue"
          >
            <div style={{ lineHeight: '0.5' }}>←</div><div className="hover:underline">Use Case Register</div>
          </Link>
          <Link
            to={stakeholderRegisterHref}
            className="text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
          >
            ← Stakeholder Register
          </Link>
        </div>

        <div className="mb-4 flex items-start gap-[14px]">
          <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[9px] bg-soft-gray">
            <span className="aq-icon text-midnight-navi text-[23px]" aria-hidden>
              warning
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-[21px] leading-[1.2] tracking-[-0.3px] font-semibold text-midnight-navi">
              Stakeholder Impact Assessment
            </h1>
            <p className="mt-1 text-xs text-gray-500">{summaryLine}</p>
          </div>
        </div>

        <section
          id="harm-summary-card"
          className="mb-4 flex flex-wrap items-center gap-2 rounded-[10px] border bg-white px-4 py-2.5"
          style={{ borderColor: 'rgba(0,0,0,0.08)' }}
          aria-label="Harm severity summary"
        >
          <span className="mr-1 text-xxs font-semibold text-[#5F5E5A]">
            Impact severity:
          </span>
          <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px]">
            {([
              { key: 'critical' as const, label: 'Critical', count: counts.critical },
              { key: 'major' as const, label: 'High', count: counts.major },
              { key: 'moderate' as const, label: 'Moderate', count: counts.moderate },
              { key: 'minor' as const, label: 'Low', count: counts.minor },
            ]).map((c) => (
              <span key={c.key} className="inline-flex items-center gap-[6px]">
                <span
                  className={HS_PILL_CLASS}
                  style={{
                    backgroundColor: 'var(--soft-gray)',
                    color: 'var(--text)',
                    borderColor: 'var(--soft-gray)',
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="inline-block h-[6px] w-[6px] rounded-[6px]"
                    style={{ backgroundColor: HS_PILL_STYLE[c.key].fg }}
                  />
                  <span>{c.label}</span>
                </span>
                <span className="tabular-nums text-xxs font-semibold text-gray-500">
                  × {c.count}
                </span>
              </span>
            ))}
          </div>
        </section>

        <section
          className="mb-10 rounded-xl border border-gray-200/90 bg-white px-5 py-2 shadow-xs md:px-8"
          aria-labelledby="risk-concentration-heading"
        >
          <div className="flex gap-8 items-center">
            <div className="flex justify-center lg:justify-start">
              <StakeholderRiskConcentrationRadar data={stakeholderRiskConcentrationData} />
            </div>
            <div className="py-2">
              <h2
                id="risk-concentration-heading"
                className="text-base font-bold text-gray-900"
              >
                {RISK_CONCENTRATION_TITLE}
              </h2>
              <div
                className="mt-3 text-xs leading-relaxed text-gray-600"
                dangerouslySetInnerHTML={{ __html: riskConcentrationHtml }}
              />
            </div>
          </div>
        </section>

        {impactPlusReportId && isLoadingReport && !impactPlusReport && (
          <div className="mb-6 animate-pulse space-y-3">
            <div className="h-24 rounded-xl bg-gray-100" />
          </div>
        )}

        {impactPlusReportId && !isLoadingReport && reportError && !impactPlusReport && (
          <div
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-xs"
            role="alert"
          >
            <div className="font-semibold">Could not load Impact+ report</div>
            <div className="mt-1">{reportError}</div>
            <button
              type="button"
              onClick={() => void loadImpactPlusReport()}
              className="mt-3 text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        <section aria-labelledby="harm-table-heading">
          <h2 id="harm-table-heading" className="sr-only">
            Identified harms
          </h2>

          <div id="harm-filter-bar" className="mb-6 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500">Impact rank:</span>
            <div className="flex flex-wrap gap-2">
              {RANK_FILTERS.map(({ key, label }) => {
                const active = key === 'all'
                  ? selectedRanks.size === 0
                  : selectedRanks.has(key as HarmRank);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      if (key === 'all') {
                        setSelectedRanks(new Set());
                      } else {
                        setSelectedRanks((prev) => {
                          const next = new Set(prev);
                          if (next.has(key as HarmRank)) {
                            next.delete(key as HarmRank);
                          } else {
                            next.add(key as HarmRank);
                          }
                          return next;
                        });
                      }
                    }}
                    className={`rounded-[6px] px-3.5 py-1.5 text-xs font-semibold transition-colors ${active
                      ? 'bg-slate-gray text-deep-blue shadow-sm'
                      : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    aria-pressed={active}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200/90 bg-white shadow-xs">
            <table className="w-full min-w-[980px] table-fixed border-collapse text-left">
              <thead>
                <tr className="border-b border-gray-200 bg-[var(--table-header-background)]">
                  <th className="w-[7%] py-3.5 pl-2 text-xxs font-semibold uppercase tracking-wider text-gray-400 md:pl-3">
                    Impact rank
                  </th>
                  <th className="w-[13%] py-3.5 pl-2 text-xxs font-semibold uppercase tracking-wider text-gray-400">
                    Stakeholder group
                  </th>
                  <th className="w-[28%] py-3.5 pl-2 text-xxs font-semibold uppercase tracking-wider text-gray-400">
                    Impact description
                  </th>
                  <th className="w-[18%] py-3.5 pl-2 text-xxs font-semibold uppercase tracking-wider text-gray-400">
                    Impact summary
                  </th>
                  <th className="w-[24%] py-3.5 pl-3 pr-5 text-xxs font-semibold uppercase tracking-wider text-gray-400 md:pr-3">
                    Detail &amp; reasoning
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row: StakeholderHarmRow, idx: number) => {
                  return (
                    <Fragment key={row.id}>
                      <tr
                        className={`border-b border-gray-100 last:border-0 ${idx % 2 === 1 ? 'bg-[var(--table-header-background)]' : 'bg-white'}`}
                      >
                        <td className="py-3.5 pl-3 align-top md:pl-3">
                          <span
                            className={HS_PILL_CLASS}
                            style={{
                              backgroundColor: 'var(--soft-gray)',
                              color: 'var(--text)',
                              borderColor: 'var(--soft-gray)',
                            }}
                          >
                            <span
                              aria-hidden="true"
                              className="inline-block h-[6px] w-[6px] rounded-[6px]"
                              style={{ backgroundColor: HS_PILL_STYLE[row.rank].fg }}
                            />
                            <span>{RANK_PILL[row.rank].label}</span>
                          </span>
                        </td>
                        <td className="py-3.5 pl-2 align-top">
                          <span className="text-xs text-gray-900">
                            <strong>{row.stakeholderGroup}</strong>
                          </span>
                        </td>
                        <td className="py-3.5 pl-2 align-top">
                          <ul className="list-disc pl-4 text-xs leading-relaxed text-gray-700">
                            {row.hazardDescriptions.map((desc, i) => (
                              <li key={i}>{desc}</li>
                            ))}
                          </ul>
                        </td>
                        <td className="py-3.5 pl-2 align-top">
                          <p className="text-xs leading-relaxed text-gray-700">
                            <strong>{row.harmDescription}</strong>
                          </p>
                        </td>
                        <td className="py-3.5 pl-3 pr-5 align-top md:pr-3">
                          <p className="text-xs leading-relaxed text-gray-700">
                            {row.detailAndReasoning}
                          </p>
                        </td>
                      </tr>

                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
